import {
  type BuyerPublicBidDetailDto,
  type MinimumRequirementDto,
  MinimumRequirementResponseType,
  type PublicBidMinimumRequirementResponseDto,
} from "../../../../autogen/bff-api";

// A plain YES_NO minimum requirement is a hard pass/fail condition, so the supplier's own answer
// is the verdict — there is nothing for the buyer to decide. A YES_NO_COMMENT requirement lets the
// buyer judge whether the comment still qualifies the supplier, so its verdict is the buyer's.
export const effectiveMinimumRequirementVerdict = (
  responseType: MinimumRequirementResponseType,
  response: PublicBidMinimumRequirementResponseDto | undefined,
): boolean | null =>
  responseType === MinimumRequirementResponseType.YesNo
    ? response?.answer ?? null
    : response?.buyerVerdict ?? null;

export interface BidMinimumRequirementProgress {
  fulfilled: number;
  notFulfilled: number;
  pending: number;
  reviewed: number;
  total: number;
  hasRequirements: boolean;
}

const responsesById = (detail: BuyerPublicBidDetailDto) =>
  new Map((detail.bid.minimumRequirementResponses ?? []).map((r) => [r.minimumRequirementId, r] as const));

export const getBidMinimumRequirementProgress = (detail: BuyerPublicBidDetailDto): BidMinimumRequirementProgress => {
  const requirements = detail.minimumRequirements ?? [];
  const byId = responsesById(detail);
  const verdicts = requirements.map((r) => effectiveMinimumRequirementVerdict(r.responseType, byId.get(r.id ?? "")));
  const fulfilled = verdicts.filter((v) => v === true).length;
  const notFulfilled = verdicts.filter((v) => v === false).length;
  const pending = verdicts.length - fulfilled - notFulfilled;
  return {
    fulfilled,
    notFulfilled,
    pending,
    reviewed: fulfilled + notFulfilled,
    total: requirements.length,
    hasRequirements: requirements.length > 0,
  };
};

export interface BidMinimumRequirementRow {
  requirement: MinimumRequirementDto;
  response: PublicBidMinimumRequirementResponseDto | undefined;
}

export const getBidMinimumRequirementRows = (detail: BuyerPublicBidDetailDto): BidMinimumRequirementRow[] => {
  const byId = responsesById(detail);
  return (detail.minimumRequirements ?? []).map((requirement) => ({
    requirement,
    response: byId.get(requirement.id ?? ""),
  }));
};
